import { css, jsx } from '@emotion/core';
import { Icon, Skeleton } from 'antd';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';

import AllTopWriters from '@/src/advocacy/components/state/AllTopWriters';
import SourceList from '@/src/advocacy/components/state/SourceList';
import StateLinks from '@/src/advocacy/components/state/StateLinks';
import StateTabs from '@/src/advocacy/components/state/StateTabs';
import { generateHTMLString } from '@/src/pdf/templates/advocacyState';
import { convertHtmlToPdf } from '@/src/pdf/utils';
import Button from '@/src/shared/components/Button';
import ContactCard from '@/src/shared/components/ContactCard';
import ContactLinks from '@/src/shared/components/ContactLinks';
import Container from '@/src/shared/components/Container';
import Head from '@/src/shared/components/Head';
import Heading from '@/src/shared/components/Heading';
import Hero from '@/src/shared/components/Hero';
import Layout from '@/src/shared/components/Layout';
import Link from '@/src/shared/components/Link';
import Section from '@/src/shared/components/Section';
import WebLogImage from '@/src/shared/components/WebLogImage';
import { bp } from '@/styles';
import request from '@/utils/axios';
import states from '@/utils/states';

const style = {
  inner: css`
    background-color: white;
    margin: -20px -16px 0;
    padding: 24px 16px;
    @media ${bp.md} {
      padding: 24px 32px;
      margin: -40px 0 0;
    }
    .pagebreak {
      page-break-before: always;
    }
  `,
  buttons: css`
    margin: 30px 0 0;
    @media ${bp.xs} {
      margin: 0;
    }
    @media print {
      display: none;
    }
  `,
  printOnly: css`
    display: none;
    @media print {
      display: block;
    }
  `,
  naicBlock: css`
    p {
      margin-bottom: 16px;
    }
    max-width: 768px;
    margin: 0 auto;
  `,
  header: css`
    @media ${bp.xs} {
      display: flex;
      justify-content: space-between;
      width: 100%;
      align-items: center;
    }
  `,
  contacts: css`
    padding: 16px 0px;

    @media ${bp.md} {
      padding: 24px 32px;
    }
    @media ${bp.lg} {
      padding: 52px 32px 52px 168px;
    }
  `,
  heroinner: {
    container: css`
      margin: 32px 0 0;
      width: 100%;
      display: block;
      @media ${bp.md} {
        display: flex;
        justify-content: space-between;
        flex-wrap: wrap;
      }
    `,
    lhs: css`
      flex: 1;
      margin-bottom: 16px;
    `,
    rhs: css`
      flex: 0 0 auto;
      margin-bottom: 16px;
      .ant-btn-group {
        @media print {
          display: none;
        }
      }
    `,
  },
};

const APCIA_COMPANY_ID = 4274;

function StateAdvocacy(props) {
  const { statecode } = props;
  const router = useRouter();
  const [stateData, setStateData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [description, setDescription] = useState('');
  const [relatedLinks, setRelatedLinks] = useState([]);
  const [pdfExportStatus, setPdfExportStatus] = useState(false);
  const [contacts, setContacts] = useState([]);

  const _statecode = (statecode || router.query.slug || '').toUpperCase();
  const isNAIC = _statecode === 'NAIC';

  useEffect(() => {
    if (!_statecode) return;
    (async function mounted() {
      try {
        const snapshotResponse = await request({
          url: 'statesnapshot/statesnapshot',
          params: {
            stateCode: _statecode,
          },
        });

        const { data } = snapshotResponse;

        setDescription(data[0].Description);

        setStateData(data);

        if (data) {
          const stateContacts = [];
          for (let i = 0; i < data.length; i += 1) {
            const { RegionalManager, LegislativeAnalyst } = data[i];

            if (RegionalManager) {
              for (let r = 0; r < RegionalManager.length; r += 1) {
                const {
                  Name,
                  WorkPhone,
                  EmailAddress,
                  Title,
                  PersonId,
                  StaffContactDisplayPicture,
                  StaffContactInitials,
                } = RegionalManager[r];

                stateContacts.push({
                  fullname: Name,
                  phone: WorkPhone,
                  email: EmailAddress,
                  title: Title,
                  id: PersonId,
                  displayPicture: StaffContactDisplayPicture === 'Y',
                  initials: StaffContactInitials,
                  companyid: APCIA_COMPANY_ID,
                });
              }
            }

            if (LegislativeAnalyst) {
              for (let l = 0; l < LegislativeAnalyst.length; l += 1) {
                const {
                  Name,
                  WorkPhone,
                  EmailAddress,
                  Title,
                  PersonId,
                  StaffContactDisplayPicture,
                  StaffContactInitials,
                } = LegislativeAnalyst[l];

                stateContacts.push({
                  fullname: Name,
                  phone: WorkPhone,
                  email: EmailAddress,
                  title: Title,
                  id: PersonId,
                  displayPicture: StaffContactDisplayPicture === 'Y',
                  initials: StaffContactInitials,
                  companyid: APCIA_COMPANY_ID,
                });
              }
            }
          }
          setContacts(stateContacts);
        }

        const relatedLinksResponse = await request({
          url: 'cms/staterelatedlinks',
          params: {
            stateCode: _statecode,
          },
        });
        setRelatedLinks(
          (relatedLinksResponse.data.StateRelatedLinkResults || []).filter(rl => rl.RelatedLinkId)
        );
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    })();
  }, [_statecode]);

  const handleExportToPdf = async () => {
    setPdfExportStatus(true);
    const fileName = `State Overview - ${description}.pdf`;
    const html = generateHTMLString({
      description,
      isNAIC,
      stateData,
      stateCode: _statecode,
      relatedLinks,
    });
    await convertHtmlToPdf({ html, fileName }, setPdfExportStatus);
  };

  return (
    <Layout>
      {!loading ? <Head title={`${description} State Landing`} /> : null}
      <Hero title={loading ? <Skeleton /> : `${description} State Overview`}>
        <div css={style.heroinner.container}>
          <div css={style.heroinner.lhs}>
            <ContactLinks title={`Contact${contacts.length > 1 ? 's' : ''}:`} contacts={contacts} />
          </div>
          <div css={style.heroinner.rhs}>
            <Button.Group css={style.buttons}>
              <Button onClickPrint>
                <Icon type="printer" theme="filled" /> Print
              </Button>
              <Button
                onClickExportPDF={handleExportToPdf}
                disabled={pdfExportStatus || !_statecode || loading}
              >
                <Icon type="file-pdf" theme="filled" /> Export PDF
              </Button>
            </Button.Group>
          </div>
        </div>
      </Hero>
      <Section bgColor="gray">
        <Container>
          <div css={style.inner}>
            <StateTabs loading={loading} tabs={stateData} />
            {isNAIC ? (
              <div css={style.naicBlock}>
                <p>
                  The National Association of Insurance Commissioners (NAIC) provides communication,
                  coordination and uniformity in financial reporting requirements required by
                  states. It has a role in the development of model laws and regulations, financial
                  reporting forms, accounting procedures and general policy guidelines for insurance
                  regulation.
                </p>
                <p>
                  Below are links to APCIA documents and other NAIC resources. For questions, please
                  contact{' '}
                  <Link anchor href="mailto:frank.obrien@apci.org">
                    Frank O&apos;Brien
                  </Link>
                  , APCIA department vice president, state government relations, at 303-830-6772.
                </p>
              </div>
            ) : null}
            <StateLinks loading={loading} stateCode={_statecode} relatedLinks={relatedLinks} />
            <div css={style.printOnly}>
              <AllTopWriters statecode={_statecode} />
              <div className="pagebreak" />
              <div>
                <Heading type={3} theme="gray" weight="bolder">
                  Additional Sources List
                </Heading>
                <SourceList size="small" />
              </div>
            </div>

            {contacts && contacts.length ? (
              <div css={style.contacts}>
                <ContactCard
                  title={`Contact${contacts.length > 1 ? 's' : ''}`}
                  contacts={contacts}
                />
              </div>
            ) : null}
          </div>
        </Container>
      </Section>
      <WebLogImage browserPath title={`${description} State Landing`} sitePageId={_statecode} />
    </Layout>
  );
}

export async function getStaticPaths() {
  return {
    paths: states.map(s => ({ params: { slug: s.StateCode } })),
    fallback: false,
  };
}

export async function getStaticProps({ params }) {
  return {
    props: {
      statecode: params.slug || 0,
    },
  };
}

export default StateAdvocacy;
